import {
  alpha,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Hidden,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Email, Lock, Visibility, VisibilityOff } from "@mui/icons-material";
import LogoIcon from "components/utils/LogoIcon";
import { useForm, SubmitHandler } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { login as loginApi } from "apis";
import { AuthState, login } from "store/features/auth";
import { RootState } from "store";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

const FLAG_MAP: Record<string, string> = {
  en: "../../gb.svg",
  fr: "../../fr.svg",
  ar: "../../ar.svg",
};

const LANGUAGES = [
  { code: "en", native: "English" },
  { code: "fr", native: "Français" },
  { code: "ar", native: "العربية" },
];

export interface LoginInput {
  email: string;
  password: string;
}

const LoginPanel: React.FC = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const dir = i18n.dir(i18n.language);
  const [lng, setLng] = useState(i18n.language);
  i18n.on("languageChanged", (lng) => setLng(`${lng}`));

  const { register, handleSubmit } = useForm<LoginInput>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useSelector<RootState, AuthState>((store) => store.auth);
  const { enqueueSnackbar } = useSnackbar();

  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);

  const requestedRedirect = searchParams.get("redirect");
  const safeRedirect =
    requestedRedirect &&
    requestedRedirect.startsWith("/") &&
    !requestedRedirect.startsWith("//")
      ? requestedRedirect
      : "/timeline";

  useEffect(() => {
    document.documentElement.dir = dir;
    if (auth.token) navigate(safeRedirect);
  }, [navigate, auth, dir, safeRedirect]);

  const formSubmit: SubmitHandler<LoginInput> = (data) => {
    setSubmitting(true);
    loginApi(data)
      .then(({ data }) => {
        enqueueSnackbar(t("login.success"), { variant: "success" });
        dispatch(login(data));
        navigate(safeRedirect);
      })
      .catch((err) => {
        console.error(
          "LOGIN ERROR:",
          JSON.stringify(err, Object.getOwnPropertyNames(err)),
        );
        const raw =
          err.response?.data?.message || err.message || "Unknown error";
        const msg = t(raw, { defaultValue: raw }) as string;
        enqueueSnackbar(msg, { variant: "error" });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: "420px" },
        maxWidth: { xs: "100%", sm: "420px" },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        [theme.breakpoints.down("sm")]: {
          bgcolor: "background.paper",
        },
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        flexGrow={1}
        alignItems="center"
        justifyContent="center"
        sx={{ overflow: "auto", px: 4, py: 4 }}
      >
        {/* Logo */}
        <Box mb={1}>
          <LogoIcon width={80} height={80} />
        </Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {t("appName")}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={4}>
          {t("login.statement")}
        </Typography>

        {/* Form */}
        <Box
          component="form"
          onSubmit={handleSubmit(formSubmit)}
          width="100%"
          maxWidth={340}
        >
          <TextField
            label={t("inputs.email")}
            fullWidth
            size="small"
            variant="outlined"
            {...register("email")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ fontSize: 18, color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
          <TextField
            label={t("inputs.password")}
            fullWidth
            size="small"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            {...register("password")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ fontSize: 18, color: "text.disabled" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOff sx={{ fontSize: 18 }} />
                    ) : (
                      <Visibility sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />

          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{
              py: 1.3,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 15,
              boxShadow: "none",
              "&:hover": { boxShadow: 2 },
            }}
          >
            {submitting ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              t("login.btnText")
            )}
          </Button>
        </Box>

        {/* Language selector */}
        <Hidden smDown>
          <Box mt={4}>
            <Button
              size="small"
              onClick={(e) => setLangAnchor(e.currentTarget)}
              sx={{
                px: 1.5,
                py: 0.6,
                borderRadius: 5,
                bgcolor: alpha(theme.palette.text.primary, 0.06),
                color: "text.secondary",
                textTransform: "uppercase",
                fontSize: 12,
                fontWeight: 600,
                gap: 0.8,
                "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.1) },
              }}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  overflow: "hidden",
                  flexShrink: 0,
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <img
                  src={FLAG_MAP[lng] ?? FLAG_MAP.en}
                  alt={lng}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </Box>
              {lng}
            </Button>
            <Menu
              open={Boolean(langAnchor)}
              anchorEl={langAnchor}
              onClose={() => setLangAnchor(null)}
              PaperProps={{ sx: { borderRadius: 2, minWidth: 180, mt: 1 } }}
              transformOrigin={{ horizontal: "center", vertical: "top" }}
              anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
            >
              {LANGUAGES.map(({ code, native }) => (
                <MenuItem
                  key={code}
                  onClick={() => {
                    i18n.changeLanguage(code);
                    setLangAnchor(null);
                  }}
                  selected={lng === code}
                  sx={{ borderRadius: 1, mx: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={FLAG_MAP[code]}
                        alt={native}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    </Box>
                  </ListItemIcon>
                  <ListItemText>
                    <Typography variant="body2">{native}</Typography>
                  </ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Hidden>
      </Box>
    </Box>
  );
};

export default LoginPanel;
